// ─── Cockpit del Contratista · Trayectoria de certificación ────────────────
// Monitorización continua de la desviación entre la puntuación acreditada por
// el expediente y el nivel BREEAM objetivo comprometido con el promotor.
//
// Concepto (Figura 2 del whitepaper BREEAM Monitor V4):
//   · Curva objetivo · línea recta hasta objetivo_pct en el hito Post-construcción
//   · Curva real · escalones que suben cada vez que se carga un documento y
//     se re-evalúan criterios (snapshots en cc_trayectoria_snapshots)
//   · Zona de desviación · sombreado entre ambas curvas cuando real < objetivo
//   · Alerta · cuando desviación > umbral_alerta_pts (default 5 pts)
//
// Datos:
//   · SELECT cc_trayectoria_snapshots WHERE expediente_id = ? ORDER BY fecha
//   · RPC cc_snapshot_trayectoria(expediente_id, evento_tipo) para refrescar
//
// Se llama desde:
//   · Tab "Trayectoria" del Cockpit (manual, botón Actualizar)
//   · Automáticamente tras cc-extract-doc con éxito (hook en index.js)

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  ChartLineUp, Warning, CheckCircle, ArrowClockwise, Target, Info,
} from '@phosphor-icons/react'
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Scatter, ReferenceLine,
} from 'recharts'
import { supabase } from '../../lib/supabase'

// Misma paleta del Cockpit
const C = {
  bg: '#FAF7F3', card: 'white', border: '#DDD5C8', text: '#3B2F2F',
  muted: '#7A6A5A', accent: '#1A3A4E', warn: '#D4614A', olive: '#8D9A2E',
  blueLight: '#EBF3F8', amber: '#C9A961',
  oliveDark: '#6A7821', oliveMed: '#8C992E', coral: '#C46B4A',
  beigeLight: '#F5F0E9', beigeDark: '#B0A79D',
}
const FF = "'DM Sans', sans-serif"

// ── Fases del ciclo de vida (mismo orden y etiquetas que la migración) ──
const FASES_ORDEN = [
  { code: 'DISENO_BASICO',      label: 'Diseño Básico',      orden: 1 },
  { code: 'DISENO_EJECUTIVO',   label: 'Diseño Ejecutivo',   orden: 2 },
  { code: 'LICITACION',         label: 'Licitación',         orden: 3 },
  { code: 'INICIO_OBRA',        label: 'Inicio Obra',        orden: 4 },
  { code: 'CIERRE_ESTRUCTURA',  label: 'Cierre Estructura',  orden: 5 },
  { code: 'INSTALACIONES',      label: 'Instalaciones',      orden: 6 },
  { code: 'ENTREGA_OBRA',       label: 'Entrega Obra',       orden: 7 },
  { code: 'POST_CONSTRUCCION',  label: 'Post-construcción',  orden: 8 },
]
const FASE_BY_CODE = Object.fromEntries(FASES_ORDEN.map(f => [f.code, f]))

// ── Componente principal ───────────────────────────────────────────────
export default function TrayectoriaCertificacion({ expediente, projectId }) {
  const [snapshots, setSnapshots] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)

  const cargarSnapshots = useCallback(async () => {
    if (!expediente?.id) return
    setLoading(true); setError(null)
    const { data, error } = await supabase
      .from('cc_trayectoria_snapshots')
      .select('*')
      .eq('expediente_id', expediente.id)
      .order('fecha', { ascending: true })
    if (error) setError(error.message)
    else setSnapshots(data || [])
    setLoading(false)
  }, [expediente?.id])

  useEffect(() => { cargarSnapshots() }, [cargarSnapshots])

  const refrescar = async () => {
    if (!expediente?.id || refreshing) return
    setRefreshing(true); setError(null)
    try {
      const { error } = await supabase.rpc('cc_snapshot_trayectoria', {
        p_expediente_id: expediente.id,
        p_evento_tipo:   'evaluacion_actualizada',
        p_evento_desc:   'Refresco manual desde tab Trayectoria',
      })
      if (error) throw error
      await cargarSnapshots()
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setRefreshing(false)
    }
  }

  // ── Datos para el chart ────────────────────────────────────────────────
  // Eje X = índice ordinal (0..N-1) de los snapshots ordenados por fecha.
  // Curva objetivo = ritmo ideal de acreditación: sube linealmente desde 0%
  // (primer snapshot) hasta objetivo_pct (último snapshot). Así ambas curvas
  // arrancan en 0 y en cualquier momento la desviación real = acreditado - ideal
  // refleja si vamos por delante o por detrás del ritmo esperado.
  const chartData = useMemo(() => {
    const objetivo = expediente?.objetivo_pct != null ? Number(expediente.objetivo_pct) : null
    const n = snapshots.length
    return snapshots.map((s, i) => {
      const idealAqui = objetivo != null && n > 1 ? (i / (n - 1)) * objetivo : 0
      const real = Number(s.puntuacion_ponderada) || 0
      const desviacionRitmo = objetivo != null ? real - idealAqui : null
      return {
        x: i,
        fecha: s.fecha,
        fase: FASE_BY_CODE[s.fase]?.label || s.fase,
        fase_code: s.fase,
        real,
        objetivo_ideal: idealAqui,
        alerta: !!s.en_alerta,
        desviacion_final: s.desviacion_pts != null ? Number(s.desviacion_pts) : null,
        desviacion_ritmo: desviacionRitmo,
        evento_tipo: s.evento_tipo,
        evento_desc: s.evento_descripcion,
        criterios_evaluados: s.criterios_evaluados,
      }
    })
  }, [snapshots, expediente?.objetivo_pct])

  // Última fila para KPIs
  const ultimo = chartData[chartData.length - 1]
  const puntos_actual = ultimo?.real ?? 0
  const objetivo_final = expediente?.objetivo_pct != null ? Number(expediente.objetivo_pct) : null
  // Brecha = diferencia directa entre puntuación acreditada y objetivo comprometido
  const brecha = ultimo && objetivo_final != null ? (puntos_actual - objetivo_final) : null
  const en_alerta = !!ultimo?.alerta

  // ── Renderizado ─────────────────────────────────────────────────────
  if (loading) {
    return <Panel><p style={{ color: C.muted, fontSize: '0.85rem' }}>Cargando trayectoria…</p></Panel>
  }

  if (!expediente?.id) {
    return (
      <Panel>
        <EmptyState
          icon={ChartLineUp}
          title="Configura primero el expediente"
          hint="La trayectoria aparecerá cuando definas el nivel BREEAM objetivo en la pestaña Setup." />
      </Panel>
    )
  }

  if (objetivo_final == null) {
    return (
      <Panel>
        <EmptyState
          icon={Target}
          title="Nivel BREEAM objetivo no definido"
          hint="Selecciona un nivel objetivo (Pass, Good, Very Good, Excellent u Outstanding) en el Setup del expediente para poder trazar la curva." />
      </Panel>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontFamily: FF }}>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
        <Kpi label="Puntuación acreditada" value={`${puntos_actual.toFixed(1)}%`}
             color={C.oliveDark} icon={ChartLineUp} />
        <Kpi label="Objetivo comprometido" value={`${objetivo_final.toFixed(0)}%`}
             color={C.olive} icon={Target}
             sub={nivelLabel(expediente?.nivel_objetivo)} />
        <Kpi label="Fase actual" value={ultimo?.fase || 'Diseño Básico'}
             color={C.accent} icon={ArrowClockwise}
             sub={`Snapshot ${chartData.length} de ${chartData.length}`} />
        <Kpi label="Estado" value={en_alerta ? 'Alerta' : 'En trayectoria'}
             color={en_alerta ? C.coral : C.oliveDark}
             icon={en_alerta ? Warning : CheckCircle}
             sub={brecha != null && !Number.isNaN(brecha) ? `${brecha >= 0 ? '+' : ''}${brecha.toFixed(1)} pts vs. objetivo` : null} />
      </div>

      {/* Cabecera + botón refrescar */}
      <div style={{ ...cardStyle, padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 600, color: C.text }}>
              Curva de trayectoria BREEAM ES · {expediente.esquema_label || 'BREEAM ES NC 2015'}
            </h3>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: C.muted }}>
              Cada punto es una re-evaluación disparada por la carga de un documento en el expediente.
            </p>
          </div>
          <button
            onClick={refrescar}
            disabled={refreshing}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              background: 'transparent', color: C.oliveDark,
              border: `1px solid ${C.oliveDark}`, borderRadius: 4,
              padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600,
              cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1,
            }}>
            <ArrowClockwise size={14} weight="bold" />
            {refreshing ? 'Actualizando…' : 'Actualizar ahora'}
          </button>
        </div>

        {error && (
          <div style={{ background: '#FDF5F3', color: C.warn, padding: '0.5rem 0.75rem', borderRadius: 4, fontSize: '0.78rem', marginTop: '0.5rem' }}>
            {error}
          </div>
        )}

        {/* Alerta destacada si aplica */}
        {en_alerta && brecha != null && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
            background: '#FFF3EE', border: `1px solid ${C.coral}`, borderRadius: 6,
            padding: '0.65rem 0.85rem', marginTop: '0.5rem',
          }}>
            <Warning size={20} color={C.coral} weight="fill" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: C.coral }}>
                Alerta de desviación · {Math.abs(brecha).toFixed(1)} pts por debajo del objetivo teórico
              </div>
              <div style={{ fontSize: '0.75rem', color: C.text, marginTop: '0.2rem' }}>
                Umbral configurado: {expediente.umbral_alerta_pts ?? 5} pts.
                Aún hay margen para corregir antes del cierre — revisa qué categorías están en riesgo en el Dashboard.
              </div>
            </div>
          </div>
        )}

        {/* Chart · Eje X = tiempo real · curva objetivo = línea horizontal en el umbral */}
        <div style={{ marginTop: '1rem', width: '100%', height: 380 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
              <CartesianGrid stroke="#EDE7DE" strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                type="number"
                domain={[0, Math.max(1, chartData.length - 1)]}
                allowDecimals={false}
                tickFormatter={i => {
                  const p = chartData[Math.round(i)]
                  return p ? new Date(p.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : ''
                }}
                tick={{ fill: C.muted, fontSize: 11, fontFamily: FF }}
                stroke={C.border}
                minTickGap={30}
              />
              <YAxis
                domain={[0, Math.max(85, objetivo_final + 15)]}
                tickFormatter={v => `${v}%`}
                tick={{ fill: C.muted, fontSize: 11, fontFamily: FF }}
                stroke={C.border}
                width={50}
              />
              <Tooltip content={CustomTooltip} cursor={{ stroke: C.olive, strokeDasharray: '3 3' }} />
              <Legend
                wrapperStyle={{ paddingTop: 8, fontSize: 12, fontFamily: FF, color: C.muted }}
                iconType="line"
                payload={[
                  { value: `Ritmo ideal · llegar a ${nivelLabel(expediente?.nivel_objetivo)} (${objetivo_final.toFixed(0)}%)`, type: 'line', color: C.oliveDark },
                  { value: 'Puntuación acreditada', type: 'line', color: C.oliveMed },
                  ...(chartData.some(p => p.alerta) ? [{ value: 'Snapshots en alerta', type: 'circle', color: C.coral }] : []),
                ]} />

              {/* Línea del umbral objetivo · horizontal punteada */}
              <ReferenceLine
                y={objetivo_final}
                stroke={C.oliveDark}
                strokeWidth={1}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: `Umbral ${nivelLabel(expediente?.nivel_objetivo)} · ${objetivo_final.toFixed(0)}%`,
                  position: 'insideTopRight',
                  fill: C.oliveDark,
                  fontSize: 10,
                  fontFamily: FF,
                  fontStyle: 'italic',
                }}
              />

              {/* Curva de ritmo ideal · progresión lineal esperada */}
              <Line
                type="linear"
                dataKey="objetivo_ideal"
                stroke={C.oliveDark}
                strokeWidth={2.5}
                dot={false}
                activeDot={false}
                name="Ritmo ideal"
                isAnimationActive={false} />

              {/* Curva real · escalones */}
              <Line
                type="stepAfter"
                dataKey="real"
                stroke={C.oliveMed}
                strokeWidth={3}
                dot={{ r: 4, fill: C.oliveMed, stroke: C.oliveDark, strokeWidth: 1 }}
                activeDot={{ r: 6 }}
                name="Puntuación acreditada"
                isAnimationActive={false}
                connectNulls />

              {/* Alertas · círculos coral sobre la curva */}
              <Scatter
                data={chartData.filter(p => p.alerta)}
                dataKey="real"
                fill={C.coral}
                shape="circle"
                r={7}
                name="Snapshots en alerta"
                isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Ayuda para interpretar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.5rem', padding: '0.6rem 0.75rem', background: C.beigeLight, borderRadius: 4 }}>
          <Info size={16} color={C.muted} weight="bold" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '0.73rem', color: C.muted, lineHeight: 1.5 }}>
            La <strong style={{ color: C.oliveDark }}>línea diagonal (ritmo ideal)</strong> proyecta el avance esperado desde 0 % hasta el objetivo comprometido.
            La <strong style={{ color: C.oliveMed }}>curva escalonada</strong> es la puntuación real acreditada a lo largo del tiempo,
            con un punto por cada día en el que el expediente registró actividad. Cuando la curva real va por encima del ritmo ideal, el proyecto va por delante.
            Los <strong style={{ color: C.coral }}>puntos coral</strong> señalan snapshots por debajo del objetivo final más el umbral de alerta configurado.
          </div>
        </div>
      </div>

      {/* Timeline de eventos */}
      <TimelineEventos snapshots={snapshots} />
    </div>
  )
}

// ── Sub-componentes ───────────────────────────────────────────────────

function Kpi({ label, value, sub, color = '#3B2F2F', icon: Icon }) {
  return (
    <div style={{ ...cardStyle, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      {Icon && (
        <div style={{
          width: 40, height: 40, borderRadius: 6, background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={22} color={color} weight="bold" />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.68rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color, marginTop: 2, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  )
}

function Panel({ children }) {
  return <div style={{ ...cardStyle, padding: '2rem 1.5rem', textAlign: 'center' }}>{children}</div>
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      {Icon && <Icon size={40} color={C.muted} weight="thin" />}
      <div style={{ fontSize: '0.9rem', color: C.text, fontWeight: 600 }}>{title}</div>
      {hint && <div style={{ fontSize: '0.78rem', color: C.muted, maxWidth: 480 }}>{hint}</div>}
    </div>
  )
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  if (!p?.fecha) return null
  const fechaLabel = new Date(p.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
  return (
    <div style={{
      background: 'white', border: `1px solid ${C.border}`, borderRadius: 4,
      padding: '0.5rem 0.75rem', fontSize: '0.75rem', fontFamily: FF, color: C.text,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: 280,
    }}>
      <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{fechaLabel}</div>
      <div>Acreditado: <strong style={{ color: C.oliveMed }}>{p.real?.toFixed?.(1) ?? '—'}%</strong></div>
      {p.objetivo_ideal != null && (
        <div style={{ color: C.oliveDark }}>Ritmo ideal: {p.objetivo_ideal.toFixed(1)}%</div>
      )}
      {p.desviacion_ritmo != null && (
        <div style={{ color: p.desviacion_ritmo < 0 ? C.coral : C.oliveDark, marginTop: 2 }}>
          {p.desviacion_ritmo >= 0 ? 'Por delante' : 'Por detrás'}: {p.desviacion_ritmo >= 0 ? '+' : ''}{p.desviacion_ritmo.toFixed(1)} pts
        </div>
      )}
      {p.criterios_evaluados != null && (
        <div style={{ color: C.muted, marginTop: 4 }}>{p.criterios_evaluados} criterios evaluados</div>
      )}
      {p.evento_desc && (
        <div style={{ color: C.muted, marginTop: 4, fontStyle: 'italic', fontSize: '0.68rem' }}>
          {p.evento_desc}
        </div>
      )}
    </div>
  )
}

function TimelineEventos({ snapshots }) {
  if (!snapshots?.length) return null
  const ultimos = [...snapshots].reverse().slice(0, 8)
  return (
    <div style={{ ...cardStyle, padding: '1rem 1.25rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600, color: C.text }}>
        Últimos eventos
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {ultimos.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.6rem', borderLeft: `3px solid ${s.en_alerta ? C.coral : C.oliveMed}`, background: '#FAF7F3', borderRadius: '0 4px 4px 0' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', color: C.text }}>
                <strong>{FASE_BY_CODE[s.fase]?.label || s.fase}</strong>
                <span style={{ color: C.muted, marginLeft: '0.5rem', fontSize: '0.72rem' }}>
                  · {new Date(s.fecha).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {s.evento_descripcion && (
                <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 2 }}>{s.evento_descripcion}</div>
              )}
            </div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: s.en_alerta ? C.coral : C.oliveDark, fontFamily: "'IBM Plex Mono', monospace" }}>
              {Number(s.puntuacion_ponderada).toFixed(1)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Helper para el label legible del nivel
function nivelLabel(code) {
  return {
    PASS: 'Pass',
    GOOD: 'Good',
    VERY_GOOD: 'Very Good',
    EXCELLENT: 'Excellent',
    OUTSTANDING: 'Outstanding',
    OTRO: 'Otro',
  }[code] || code || 'Sin definir'
}

const cardStyle = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(59,47,47,0.04)',
}
